'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense, use } from 'react';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Dialog, DialogClose, DialogPortal, DialogPrimitiveContent, DialogTitle } from '@/components/ui/dialog';
import ViverseButton from '@/components/ViverseButton';
import ViverseWorldCard from '@/components/ViverseWorldCard';
import { RECOMMEND_VIEW_ALL_THRESHOLD, RECOMMEND_WORLDS_LIMIT } from '@/constants/recommend';
import { Link } from '@/i18n/navigation';
import { LogoOverlayContentKind, type LogoOverlayContentModel } from '@/lib/logo-overlay';
import type { RecommendRoom } from '@/services/cms';
type ControlBarRecommendationOverlayProps = {
  model: LogoOverlayContentModel;
  onCloseAction: () => void;
  open: boolean;
  portalContainer: HTMLElement;
  roomsPromise: Promise<RecommendRoom[]> | null;
};
type RecommendationGridProps = {
  cardType: 'experiences' | 'games';
  roomsPromise: Promise<RecommendRoom[]>;
};
type RecommendationContentProps = RecommendationGridProps & {
  ctaLabel: string;
  model: LogoOverlayContentModel;
  onCloseAction: () => void;
};
function RecommendationContent(t0) {
  const $ = _c(15);
  const {
    cardType,
    ctaLabel,
    model,
    onCloseAction,
    roomsPromise
  } = t0;
  const rooms = use(roomsPromise);
  let t1;
  if ($[0] !== cardType || $[1] !== rooms) {
    let t2;
    if ($[3] !== cardType) {
      t2 = room => <ViverseWorldCard key={room.id} cardType={cardType} className="rounded-none p-0 shadow-none lg:hover:shadow-none" hideLiked={true} hidePremiumBadge={true} imageSizes="(max-width: 767px) 50vw, 210px" info={room} mediaClassName="rounded-2xl" />;
      $[3] = cardType;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = rooms.map(t2);
    $[0] = cardType;
    $[1] = rooms;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="grid w-full max-w-[912px] grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== ctaLabel || $[8] !== model || $[9] !== onCloseAction || $[10] !== rooms.length) {
    t3 = rooms.length < RECOMMEND_VIEW_ALL_THRESHOLD && model.viewAllHref && <div className="mt-6 pb-8"><Link href={model.viewAllHref} onClick={onCloseAction}><ViverseButton variant="default" size="medium">{ctaLabel}</ViverseButton></Link></div>;
    $[7] = ctaLabel;
    $[8] = model;
    $[9] = onCloseAction;
    $[10] = rooms.length;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t2 || $[13] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
function RecommendationGridSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className="grid w-full max-w-[912px] grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">{Array.from({
        length: RECOMMEND_WORLDS_LIMIT
      }, _temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(_, index) {
  return <div key={index} className="flex flex-col gap-2"><div className="bg-vive-primary-70 aspect-430/242 animate-pulse rounded-2xl" /><div className="bg-vive-primary-70 h-4 w-3/4 animate-pulse rounded-sm" /><div className="bg-vive-primary-70 h-3 w-1/2 animate-pulse rounded-sm" /></div>;
}
export default function ControlBarRecommendationOverlay(t0) {
  const $ = _c(30);
  const {
    model,
    onCloseAction,
    open,
    portalContainer,
    roomsPromise
  } = t0;
  const t = useTranslations("exit-recommendation-modal");
  const cardType = model.contentKind === LogoOverlayContentKind.GAME ? "games" : "experiences";
  let t1;
  if ($[0] !== onCloseAction) {
    t1 = nextOpen => {
      if (!nextOpen) {
        onCloseAction();
      }
    };
    $[0] = onCloseAction;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("close");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <X className="size-6" aria-hidden="true" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <DialogClose aria-label={t2} className="bg-vive-primary-70 absolute top-7 right-6 z-10 inline-flex size-[34px] items-center justify-center rounded-full text-white">{t3}</DialogClose>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("title");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <DialogTitle className="mb-6 text-center text-2xl leading-8 font-extrabold tracking-[0.1px] text-white md:text-[30px] md:leading-10">{t5}</DialogTitle>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== cardType || $[12] !== model || $[13] !== onCloseAction || $[14] !== roomsPromise || $[15] !== t) {
    t7 = roomsPromise ? <Suspense fallback={<RecommendationGridSkeleton />}><RecommendationContent cardType={cardType} ctaLabel={t(`cta.${cardType}`)} model={model} onCloseAction={onCloseAction} roomsPromise={roomsPromise} /></Suspense> : <RecommendationGridSkeleton />;
    $[11] = cardType;
    $[12] = model;
    $[13] = onCloseAction;
    $[14] = roomsPromise;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="flex min-h-full flex-col items-center px-4 pt-20 pb-12 md:px-16 md:pt-14">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t4 || $[21] !== t8) {
    t9 = <DialogPrimitiveContent data-component="control-bar-recommendation-overlay" className="z-overlay absolute inset-0 overflow-y-auto bg-[rgba(19,27,38,0.7)] backdrop-blur-[50px] outline-none" onInteractOutside={_temp2}>{t4}{t8}</DialogPrimitiveContent>;
    $[20] = t4;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== portalContainer || $[24] !== t9) {
    t10 = <DialogPortal container={portalContainer}>{t9}</DialogPortal>;
    $[23] = portalContainer;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== open || $[27] !== t1 || $[28] !== t10) {
    t11 = <Dialog modal={false} open={open} onOpenChange={t1}>{t10}</Dialog>;
    $[26] = open;
    $[27] = t1;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
function _temp2(event) {
  return event.preventDefault();
}